import type { Formats } from 'next-intl'

/**
 * Locale-aware number/date/time format presets surfaced into the app's typed
 * i18n configuration (the `Formats` augmentation in `next-intl.d.ts`). Format
 * presets are locale-invariant declarations; the locale supplies the actual
 * regional rendering at format time.
 *
 * Kept in its own module rather than in `request.ts`: the presets are read by
 * plain library code (`@/i18n/format`) that client components pull in, and
 * `request.ts` statically imports the whole translation-store tree plus
 * `resolveFile` — which reaches `node:crypto`. Importing the request config to
 * get at a format preset would drag both into the browser bundle.
 */
export const formats = {
    dateTime: {
        short: {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        },
        /**
         * Long month + day, no year — for a date whose year the surrounding copy
         * already implies (a just-taken trip). `timeZone` is pinned here rather
         * than at the call site because that is what keeps the rendered day
         * identical across SSR and hydration
         * (`invariants.md#ssr-safe-rendered-dates`): callers format a `Date` built
         * from explicit Y/M/D parts, so a fixed zone makes the output a pure
         * function of those parts. Dropping it would reintroduce the ambient-zone
         * day shift this preset exists to prevent.
         */
        monthDay: {
            day: 'numeric',
            month: 'long',
            timeZone: 'UTC',
        },
    },
    number: {
        precise: {
            maximumFractionDigits: 2,
        },
    },
} satisfies Formats
